import React, { type FC, useCallback, useEffect, useState, useContext, type PropsWithChildren, Suspense } from 'react';

import kebabCase from 'lodash/kebabCase';
import { useInView } from 'react-intersection-observer';
import { useSmartlingStrings } from '@lucid/hooks';

import Box from '../../box/box';
import AccessibleDescription from '../../accessible-description/accessible-description';
import PauseOverlay from '../../buttons/pause-overlay';
import LayoutContext from '../../context/layout-context';
import LoadingIndicator from '../../loading-indicator/loading-indicator';

export type VisibleVideoProps = {
  /** Video source */
  src: string;
  /** Number between 0 and 1 indicating the percentage that
   * should be visible before triggering intersection observer */
  threshold?: number | number[];
  /** Pause functionality lives in parent component */
  hasExternalPauseButton?: boolean;
  /** Video title */
  title?: string | null;
  /** Video description */
  description?: string | null;
  /** Id for describedby */
  descriptionElementId?: string;
  /** Will loop video if true */
  shouldLoop?: boolean;
  /** Determines if the box shadow should be hidden */
  shouldHideBoxShadow?: boolean;
  /** Overrides Pause from parent component */
  willManualOverride?: boolean;
  /** Allows component to receive additional styles */
  className?: string;
};

const VisibleVideo: FC<PropsWithChildren<VisibleVideoProps>> = ({
  src,
  threshold = 0.5,
  title,
  hasExternalPauseButton = false,
  description,
  descriptionElementId = '',
  shouldLoop = false,
  shouldHideBoxShadow = false,
  className,
  willManualOverride = false,
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_ct_add_on_pause_animation_label',
      'mktg_ct_add_on_play_animation_label',
      'mktg_ct_add_on_restart_animation_label',
    ],
    shortCode,
  });
  const [iconLabel, setIconLabel] = useState('Pause');
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoForceStopped, setIsVideoForceStopped] = useState(willManualOverride);
  const [hasVideoFinished, setHasVideoFinished] = useState(false);
  const [canVideoPlay, setCanVideoPlay] = useState(false);
  /** Intersection Observer */
  const [ref, isInView, entry] = useInView({
    threshold: threshold,
  });

  /** Reference to video */
  const video = entry?.target as HTMLMediaElement;
  /** Video description for a11y */
  const descriptionId = description ? kebabCase(descriptionElementId) : undefined;

  const restartVideo = () => {
    startVideo();
    setHasVideoFinished(false);
    setIsVideoForceStopped(false);
  };

  const startVideo = useCallback(() => {
    video?.play().then(() => setIsVideoPlaying(true));
  }, [video]);

  const stopVideo = useCallback(() => {
    video?.pause();
    setIsVideoPlaying(false);
  }, [video]);

  const handleOnClick = () => {
    if (hasVideoFinished && !isVideoPlaying) {
      restartVideo();
    } else {
      setIsVideoForceStopped(!isVideoForceStopped);
    }
  };

  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
    setHasVideoFinished(true);
  };

  const handleVideoCanPlay = () => {
    setCanVideoPlay(true);
  };

  useEffect(() => {
    setIsVideoForceStopped(willManualOverride);
  }, [willManualOverride]);

  useEffect(() => {
    if (video) {
      // this is nessesary for iOS devices
      if (isInView && video.readyState <= 1) {
        video.load();
      }

      if (!isInView && !isVideoForceStopped) {
        video.currentTime = 0;
      }

      if (video.readyState >= video.HAVE_FUTURE_DATA) {
        setCanVideoPlay(true);
      }
    }

    if (canVideoPlay) {
      if (isInView && !hasVideoFinished && !isVideoForceStopped) {
        startVideo();
      } else if (!isInView || hasVideoFinished || isVideoForceStopped) {
        if (!isInView && hasVideoFinished) {
          setHasVideoFinished(false);
        }
        stopVideo();
      }
    }
  }, [isInView, hasVideoFinished, isVideoForceStopped, startVideo, stopVideo, canVideoPlay, video]);

  useEffect(() => {
    if (isVideoPlaying) {
      setIconLabel(withSmartling('mktg_ct_add_on_pause_animation_label') || 'Click or press Enter to pause animation');
    } else if (!isVideoPlaying && !hasVideoFinished) {
      setIconLabel(withSmartling('mktg_ct_add_on_play_animation_label') || 'Click or press Enter to play animation');
    } else {
      setIconLabel(
        withSmartling('mktg_ct_add_on_restart_animation_label') || 'Click or press Enter to restart animation',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasVideoFinished, isVideoPlaying]);

  const renderVideo = () => (
    <video
      ref={ref}
      muted
      playsInline
      aria-describedby={descriptionId}
      css={{
        width: '100%',
      }}
      loop={shouldLoop}
      onCanPlay={handleVideoCanPlay}
      onEnded={handleVideoEnd}
      preload="auto"
      src={src}
      title={title || ''}
    />
  );

  return (
    <Suspense fallback={<LoadingIndicator />}>
      <Box
        css={theme => [
          { label: 'visibleVideo', position: 'relative', height: '100%' },
          !shouldHideBoxShadow && { boxShadow: theme.shadows.nav },
        ]}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
      >
        {!canVideoPlay && (
          <Box
            css={theme => [
              {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(to right, ${theme.colors.coolGrey[5]} 10%, ${theme.colors.coolGrey[10]} 33%, ${theme.colors.coolGrey[5]} 66%)`,
                backgroundSize: '200% 100%',
                animation: `${theme.keyframe.loading} 2s linear infinite`,
              },
            ]}
          />
        )}
        {hasExternalPauseButton ? (
          renderVideo()
        ) : (
          <PauseOverlay aria-label={iconLabel} isVideoPlaying={isVideoPlaying} onClick={handleOnClick}>
            {renderVideo()}
          </PauseOverlay>
        )}
        {description && <AccessibleDescription id={descriptionId} SRText={description} />}
      </Box>
    </Suspense>
  );
};

export default VisibleVideo;
